import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Container,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useHead } from '@unhead/react';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  faqs: FAQItem[];
  title?: string;
  subtitle?: string;
  backgroundColor?: string;
}

/**
 * FAQ Section Component with Schema.org Structured Data
 * 
 * CRITICAL: Only use on PUBLIC pages that Google can crawl!
 * - ✅ Good: Homepage, market detail pages, global reports page
 * - ❌ Bad: Dashboard, subscriptions, admin pages (require auth)
 * - ❌ Bad: Checkout, login pages (marked noindex)
 * 
 * SEO Benefits:
 * - Rich snippets in search results (2-3x more SERP space)
 * - Voice search optimization (Alexa, Siri, Google Assistant)
 * - 50-80% higher click-through rates
 * - Establishes topical authority
 */
export const FAQSection: React.FC<FAQSectionProps> = ({
  faqs,
  title = 'Frequently Asked Questions',
  subtitle,
  backgroundColor = '#f8f9fa',
}) => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Generate FAQ Schema for search engines
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  // Inject FAQ structured data into page head
  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(faqSchema)
      }
    ]
  });

  return (
    <>

      <Box
        component="section"
        sx={{
          py: { xs: 6, md: 8 },
          backgroundColor,
        }}
        aria-labelledby="faq-title"
      >
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              id="faq-title"
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: subtitle ? 2 : 0,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto' }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* FAQ Accordions */}
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expanded === `panel${index}`}
                onChange={handleChange(`panel${index}`)}
                sx={{
                  mb: 2,
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: '0 0 16px 0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                  sx={{
                    minHeight: 64,
                    '&.Mui-expanded': {
                      minHeight: 64,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontWeight: 500 }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ lineHeight: 1.7 }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

/**
 * Pre-built FAQ sets for different pages
 * Remember: Only use on PUBLIC pages!
 */
export const FAQTemplates = {
  // General subscription FAQs for HOMEPAGE
  subscriptionGeneral: [
    {
      question: 'How do I access market reports?',
      answer: 'Market reports are available through our subscription service, which provides unlimited access to all available market reports. Subscriptions ensure you always have the latest data and insights for your markets of interest.',
    },
    {
      question: 'What are the subscription options?',
      answer: 'We offer monthly subscriptions at $250/month and annual subscriptions at $1,800/year (saving $1,200 compared to monthly). Both provide full access to all available market reports, monthly updates, podcasts, and market analytics.',
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, subscriptions can be easily cancelled at any time directly through our website. Simply log into your account dashboard and click "Manage Subscriptions" to cancel with just a few clicks. Monthly subscriptions cancel at the end of the current billing period. Annual subscriptions are non-refundable but can be cancelled to prevent auto-renewal.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, MasterCard, American Express) and process payments securely through Stripe. Enterprise customers can contact us for invoice billing options.',
    },
  ],

  // Market coverage FAQs for GLOBAL REPORTS PAGE
  marketCoverage: [
    {
      question: 'Which markets are covered in the reports?',
      answer: 'We currently offer 150+ comprehensive hospitality market reports covering major cities across North America. An additional 450+ global market reports covering Europe, Asia-Pacific, Middle East, Africa, and Latin America will be rolling out in Fall 2025.',
    },
    {
      question: 'How often are reports updated?',
      answer: 'All market reports are updated monthly with fresh data and insights. Subscribers receive notifications when new reports are published for their markets of interest.',
    },
    {
      question: 'What data is included in each market report?',
      answer: 'Each report includes key performance indicators (KPIs), supply and demand analysis, ADR and RevPAR trends, occupancy rates, development pipeline data, market forecasts, and expert commentary from our global team of hospitality consultants.',
    },
    {
      question: 'Can I access historical report data?',
      answer: 'Yes, subscribers have access to all historical reports that have been produced, allowing you to track market trends and performance over time. This historical data is invaluable for investment decisions and strategic planning.',
    },
  ],

  // Report content FAQs for MARKET DETAIL PAGES
  reportContent: (marketName: string) => [
    {
      question: `What's included in the ${marketName} market report?`,
      answer: `The ${marketName} report includes comprehensive KPI analysis, supply and demand metrics, ADR and RevPAR trends, occupancy performance, competitive set analysis, development pipeline overview, and 12-month forecasts specific to the ${marketName} hospitality market.`,
    },
    {
      question: `How do I access the ${marketName} report?`,
      answer: `The ${marketName} report is available through our subscription service. Once subscribed, you can instantly download the latest report as a PDF along with all historical editions. Subscriptions provide unlimited access to all available market reports.`,
    },
    {
      question: `Is the ${marketName} report included in subscriptions?`,
      answer: `Yes, all market reports including ${marketName} are included with both monthly and annual subscriptions. Subscribers get immediate access to download the latest report and all historical editions.`,
    },
    {
      question: `Who creates the ${marketName} market analysis?`,
      answer: `Our reports are created by Horwath HTL's global team of hospitality consultants with local market expertise. Each report combines proprietary data, industry partnerships, and on-the-ground insights from professionals who understand the ${marketName} market intimately.`,
    },
  ],

  // Data and methodology FAQs
  dataMethodology: [
    {
      question: 'Where does your market data come from?',
      answer: 'Our data comes from multiple authoritative sources including STR Global, tourism boards, hotel associations, proprietary surveys, and our network of consultants in 50+ countries. We triangulate multiple data sources to ensure accuracy and completeness.',
    },
    {
      question: 'How do you forecast market performance?',
      answer: 'Our forecasts use advanced econometric models combining historical performance data, forward bookings, economic indicators, tourism trends, and expert judgment from our local consultants. Models are continuously refined based on actual performance.',
    },
    {
      question: 'Can I use report data in my own presentations?',
      answer: 'Yes, subscribers have license to use report data, charts, and insights in their internal presentations and reports. We simply ask that you attribute the source as "Horwath HTL Market Reports" when using our data.',
    },
  ],
};

export default FAQSection;